import { Typography } from "@mui/material"
import { styled } from "@mui/system"
import AttachmentCard from "components/attachments/AttachmentCard"
import { InfiniteHorizontalScroll } from "components/common/InfiniteHorizontalScroll"
import useAuthRedirect from "hooks/useAuthRedirect"
import { api } from "lib/api"
import openWindowAsync from "lib/open-window-async"
import useCurrent from "lib/use-current"
import { UseMoreParams } from "models/convention-api/types"
import ConventionExhibitorProfileAttachment from "models/convention-api/v1/ConventionExhibitorProfileAttachment"
import React from "react"

interface AttachmentsHorizontalScrollProps {
  params: Partial<UseMoreParams>
}

const AttachmentsHorizontalScroll = (props: AttachmentsHorizontalScrollProps) => {
  const { params } = props
  const authRedirect = useAuthRedirect()
  const current = useCurrent()

  const attachments = ConventionExhibitorProfileAttachment.useMore({
    limit: 25,
    includeCount: true,
    ...params,
  })

  const openAttachment = (attachment: ConventionExhibitorProfileAttachment) => () => {
    authRedirect(() =>
      openWindowAsync(async () => {
        const user = current?.user
        if (user) {
          await api.createProfileAttachmentView({ req: undefined }, attachment.id, user.id)
        }
        return attachment.attachment_url || attachment.url || ""
      }),
    )
  }

  return (
    <InfiniteHorizontalScroll.Scroller
      hasMore={!!attachments.hasMore}
      loadMore={attachments.loadMore}
      isLoading={attachments.isLoadingMore}>
      <InfiniteHorizontalScroll.Heading>
        {/*
          If we need to customize heading, we can easily add it later,
          for now it's not important since we don't know when we need it
        */}
        <Heading component="h4">
          Attachments {attachments.count === undefined ? "" : `(${attachments.count ?? 0})`}
        </Heading>
      </InfiniteHorizontalScroll.Heading>
      <InfiniteHorizontalScroll.Content>
        {attachments.data?.map((attachment) => (
          <AttachmentCard
            key={attachment.id}
            name={attachment.name}
            description={attachment.description}
            type={attachment.url ? "link" : "file"}
            onClick={openAttachment(attachment)}
          />
        ))}
      </InfiniteHorizontalScroll.Content>
    </InfiniteHorizontalScroll.Scroller>
  )
}

export default AttachmentsHorizontalScroll

const Heading = styled(Typography)(({ theme }) => ({
  ...theme.fonts.raleway700,
  fontSize: "18px",
  lineHeight: "1.16",
  color: theme.palette.gray.darkest,
})) as typeof Typography
